import { useCallback, useEffect, useRef, useState, type Dispatch } from 'react';
import * as React from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { createPortal } from 'react-dom';
import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import { styled } from '@mui/material/styles';
import {
  $getSelection,
  $isLineBreakNode,
  $isRangeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type BaseSelection,
  type LexicalEditor,
} from 'lexical';

import { getSelectedNode } from '@/components/TextEditorWithLexical/utils/getSelectedNode';

import { ReactComponent as CancelSVG } from '../../images/icons/close.svg';
import { ReactComponent as EditSVG } from '../../images/icons/pencil-fill.svg';
import { ReactComponent as ConfirmSVG } from '../../images/icons/success-alt.svg';
import { ReactComponent as TrashSVG } from '../../images/icons/trash.svg';
import { setFloatingElemPositionForLinkEditor } from '../../utils/setFloatingElemPositionForLinkEditor';
import { sanitizeUrl } from '../../utils/url';

const StyledLinkEditorWrapper = styled('div')({
  display: 'flex',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
  maxWidth: '400px',
  width: '100%',
  opacity: 0,
  backgroundColor: '#fff',
  boxShadow: '0 5px 10px rgba(0, 0, 0, 0.3)',
  borderRadius: '0 0 8px 8px',
  transition: 'opacity 0.5s',
  willChange: 'transform',
  '.button.active': {
    backgroundColor: 'rgb(223, 232, 250)',
  },
  '.button': {
    width: '20px',
    height: '20px',
    display: 'inline-block',
    padding: '6px',
    borderRadius: '8px',
    cursor: 'pointer',
    margin: '0 2px',
  },
  '.button.hovered': {
    width: '20px',
    height: '20px',
    display: 'inline-block',
    backgroundColor: '#eee',
  },
  '.button i, .actions i': {
    backgroundSize: 'contain',
    display: 'inline-block',
    height: '20px',
    width: '20px',
    verticalAlign: '-0.25em',
  },
  '.link-input': {
    display: 'block',
    width: 'calc(100% - 75px)',
    boxSizing: 'border-box',
    margin: '12px 12px',
    padding: '8px 12px',
    borderRadius: '15px',
    backgroundColor: '#eee',
    fontSize: '15px',
    color: 'rgb(5, 5, 5)',
    border: '0',
    outline: '0',
    position: 'relative',
    fontFamily: 'inherit',
  },
  '.link-view': {
    display: 'block',
    width: 'calc(100% - 24px)',
    margin: '8px 12px',
    padding: '8px 12px',
    borderRadius: '15px',
    fontSize: '15px',
    color: 'rgb(5, 5, 5)',
    border: '0',
    outline: '0',
    position: 'relative',
    fontFamily: 'inherit',
  },
  '.link-view a': {
    display: 'block',
    wordBreak: 'break-word',
    width: 'calc(100% - 33px)',
  },
  div: {
    display: 'flex',
    alignItems: 'center',
  },
  'div.link-edit': {
    backgroundSize: '16px',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '16px',
    verticalAlign: '-0.25em',
    position: 'absolute',
    right: '30px',
    top: '0',
    bottom: '0',
    cursor: 'pointer',
  },
  'div.link-trash': {
    backgroundSize: '16px',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '16px',
    verticalAlign: '-0.25em',
    position: 'absolute',
    right: '0',
    top: '0',
    bottom: '0',
    cursor: 'pointer',
  },
  'div.link-cancel': {
    backgroundSize: '16px',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '16px',
    verticalAlign: '-0.25em',
    marginRight: '34px',
    position: 'absolute',
    right: '0',
    top: '0',
    bottom: '0',
    cursor: 'pointer',
  },
  'div.link-confirm': {
    backgroundSize: '16px',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '16px',
    verticalAlign: '-0.25em',
    marginRight: '12px',
    position: 'absolute',
    right: '0',
    top: '0',
    bottom: '0',
    cursor: 'pointer',
  },
  '.link-input a': {
    color: 'rgb(33, 111, 219)',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    marginRight: '30px',
    textOverflow: 'ellipsis',
  },
  '.link-input a:hover': {
    textDecoration: 'underline',
  },
  '.font-size-wrapper, .link-editor .font-family-wrapper': {
    display: 'flex',
    margin: '0 4px',
  },
  select: {
    padding: '6px',
    border: 'none',
    backgroundColor: 'rgba(0, 0, 0, 0.075)',
    borderRadius: '4px',
  },
});

const FloatingLinkEditor = ({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('https://');
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null);

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        setLinkUrl(linkParent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
      if (isLinkEditMode) {
        setEditedLinkUrl(linkUrl);
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect: DOMRect | undefined = nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode, linkUrl]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, $updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, $updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor();
    });
  }, [editor, $updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const monitorInputInteraction = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLinkSubmission();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(editedLinkUrl));
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent();
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              });
              parent.replace(linkNode, true);
            }
          }
        });
      }
      setEditedLinkUrl('https://');
      setIsLinkEditMode(false);
    }
  };

  return (
    <StyledLinkEditorWrapper ref={editorRef}>
      {!isLink ? null : isLinkEditMode ? (
        <React.Fragment>
          <input
            ref={inputRef}
            className="link-input"
            value={editedLinkUrl}
            onChange={event => {
              setEditedLinkUrl(event.target.value);
            }}
            onKeyDown={event => {
              monitorInputInteraction(event);
            }}
          />
          <div>
            <div
              className="link-cancel"
              role="button"
              tabIndex={0}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                setIsLinkEditMode(false);
              }}
            >
              <CancelSVG />
            </div>

            <div
              className="link-confirm"
              role="button"
              tabIndex={0}
              onMouseDown={event => event.preventDefault()}
              onClick={handleLinkSubmission}
            >
              <ConfirmSVG />
            </div>
          </div>
        </React.Fragment>
      ) : (
        <div className="link-view">
          <a href={sanitizeUrl(linkUrl)} target="_blank" rel="noopener noreferrer">
            {linkUrl}
          </a>
          <div
            className="link-edit"
            role="button"
            tabIndex={0}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              setEditedLinkUrl(linkUrl);
              setIsLinkEditMode(true);
            }}
          >
            <EditSVG />
          </div>
          <div
            className="link-trash"
            role="button"
            tabIndex={0}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            }}
          >
            <TrashSVG />
          </div>
        </div>
      )}
    </StyledLinkEditorWrapper>
  );
};

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>,
): JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function $updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(focusNode, $isAutoLinkNode);
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false);
          return;
        }
        const badNode = selection
          .getNodes()
          .filter(node => !$isLineBreakNode(node))
          .find(node => {
            const linkNode = $findMatchingParent(node, $isLinkNode);
            const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
            return (
              (focusLinkNode && !focusLinkNode.is(linkNode)) ||
              (linkNode && !linkNode.is(focusLinkNode)) ||
              (focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode)) ||
              (autoLinkNode && !autoLinkNode.is(focusAutoLinkNode))
            );
          });
        if (!badNode) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        payload => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  );
}

export const FloatingLinkEditorPlugin = ({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  anchorElem?: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element | null => {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode);
};
